import React from "react";
import confetti from "canvas-confetti";
import { useLocation } from "react-router-dom";
import {
	ExclamationTriangleIcon,
	InboxIcon,
	SparklesIcon,
} from "@heroicons/react/24/solid";

import Heading from "../../shared/components/UIElements/Heading";
import useApi from "../../shared/hooks/useApi";

const PaymentSucceeded = () => {
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const sessionId = query.get("session_id");
	const redirectStatus = query.get("redirect_status");

	const { data, loading, execute } = useApi();

	const isFailureByRedirect = redirectStatus === "failed";
	const customerEmail = data?.customer_email || null;
	const firstName = React.useMemo(() => {
		const fullName = data?.customer_name;
		if (!fullName || typeof fullName !== "string") return null;
		const parts = fullName.trim().split(/\s+/);
		return parts[0] || null;
	}, [data]);

	React.useEffect(() => {
		if (sessionId) {
			execute(`/api/billing/checkout-session/${sessionId}`, {
				method: "GET",
				requireAuth: false,
			});
		}
	}, [sessionId, execute]);

	const isSuccess = React.useMemo(() => {
		if (isFailureByRedirect) return false;
		if (!sessionId) return true; // Embedded flow without session_id
		if (data?.status === "complete") return true;
		if (data?.payment_status === "paid") return true;
		return false;
	}, [data, isFailureByRedirect, sessionId]);

	React.useEffect(() => {
		if (isSuccess) {
			confetti({
				particleCount: 100,
				spread: 70,
				origin: { x: 0.1, y: 0.6 },
			});
			confetti({
				particleCount: 100,
				spread: 70,
				origin: { x: 0.9, y: 0.6 },
			});

			// Track checkout completion
			if (typeof window?.datafast === "function") {
				window.datafast("complete_checkout", {
					customer_email: customerEmail,
					customer_name: data?.customer_name,
					session_id: sessionId,
				});
			}
		}
	}, [isSuccess, customerEmail, data?.customer_name, sessionId]);

	if (loading && sessionId) {
		return (
			<div className="text-left justify-center h-screen flex flex-col max-w-2xl mx-auto">
				<Heading>Je betaling wordt gecontroleerd...</Heading>
				<p className="text-gray-600 mt-2">Even geduld alsjeblieft.</p>
			</div>
		);
	}

	if (!isSuccess) {
		return (
			<div className="text-left justify-center h-screen flex flex-col max-w-2xl mx-auto">
				<div className="mb-4">
					<ExclamationTriangleIcon className="size-10 text-red-600 mb-3" />
					<Heading>Betaling mislukt of geannuleerd</Heading>
				</div>
				<p className="text-gray-700 mb-4">
					Je betaling is niet voltooid. Probeer het opnieuw of neem
					contact op met ons team als je hulp nodig hebt.
				</p>
				<a
					href="/starten"
					className="text-purple-700 underline font-medium"
				>
					Probeer opnieuw
				</a>
				<p className="text-sm text-gray-400 italic mt-6">
					Vragen? Mail ons op{" "}
					<a href="mailto:team@dentiva.nl" className="underline">
						team@dentiva.nl
					</a>
					.
				</p>
			</div>
		);
	}

	return (
		<div className="text-left justify-center h-screen flex flex-col max-w-2xl mx-auto">
			<div className="mb-4">
				<img
					src="https://storage.googleapis.com/snelterecht/assets/confetti-emoji.png"
					alt=""
					className="size-10 mb-4"
				/>
				<Heading>{`Gelukt${firstName ? `, ${firstName}` : ""}!`}</Heading>
			</div>

			<div className="flex items-start mt-2 mb-2">
				<InboxIcon className="size-5 text-brand-intense mr-3 mt-1" />
				<p className="text-gray-700 text-base mt-0.5">
					<span className="font-medium text-black">
						Check je e-mail
					</span>
					{customerEmail && (
						<>
							{" op "}
							<span className="font-medium text-black">
								{customerEmail}
							</span>
						</>
					)}
					<br />
					Daarin vind je een mailtje met link om je wachtwoord in te
					stellen.
					<br />
					Geen mail ontvangen? Kijk even in je ongewenste e-mail.
				</p>
			</div>

			<div className="flex items-start mt-2 mb-2">
				<SparklesIcon className="size-5 text-brand-intense mr-3 mt-1" />
				<p className="text-gray-700 text-base mt-0.5">
					<span className="font-medium text-black">
						Zodra je bent ingelogd, begeleiden we je stap voor stap.
					</span>
					<br />
					Binnen een paar minuten kun je met DentHR aan de slag!
				</p>
			</div>

			<div className="mt-6">
				<p className="text-sm text-gray-400 italic">
					Geen mail gekregen of andere vragen? Neem contact op met{" "}
					<a href="mailto:team@dentiva.nl" className="underline">
						team@dentiva.nl
					</a>
					.
				</p>
			</div>
		</div>
	);
};

export default PaymentSucceeded;
